// Edits a list of target rules. Each row: include/exclude · type · value.
// Recipients = anyone matching at least one "include" row and no "exclude" row.
import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, UserRound, Ban } from "lucide-react";
import SearchBar from "../../../components/SearchBar";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import { RULE_TYPES, newRule } from "../utils/rules";
import { TARGETABLE_ROLES } from "../utils/labels";
import { Select, Btn, IconBtn } from "./ui";

const UserPicker = ({ rule, onPick }) => {
  const { s, t, theme, language } = useBroadcastPage();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return undefined;
    }
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const rows = await broadcastApi.searchUsers(q.trim());
        if (id === reqId.current) setResults(rows || []);
      } catch {
        if (id === reqId.current) setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  if (rule.userId && !open) {
    return (
      <div style={{ ...s.row, flexWrap: "nowrap" }}>
        <span style={{ ...s.input, display: "flex", alignItems: "center", gap: 8, overflow: "hidden", whiteSpace: "nowrap" }}>
          <UserRound size={15} style={{ flexShrink: 0, color: s.colors.muted }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{rule._userLabel || t.rules.user(rule.userId)}</span>
        </span>
        <Btn kind="secondary" small onClick={() => setOpen(true)}>
          {t.rules.change}
        </Btn>
      </div>
    );
  }

  const nameOf = (u) => (language === "ar" && u.nameAr) || u.name || u.email || t.rules.user(u.userId);

  return (
    <div style={{ position: "relative" }}>
      <SearchBar value={q} onChange={setQ} placeholder={t.rules.searchUser} theme={theme} language={language} icon />
      {results && (
        <div className="reusable-dropdown-portal" style={s.menu}>
          {results.length === 0 && <div style={{ padding: "12px 14px", fontSize: 13.5, color: s.colors.muted }}>{t.rules.noUsers}</div>}
          {results.map((u) => {
            const label = nameOf(u);
            return (
              <button
                key={u.userId}
                type="button"
                className="bc-menu-item"
                style={s.menuItem}
                onClick={() => {
                  onPick(u.userId, label);
                  setOpen(false);
                  setQ("");
                  setResults(null);
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 12, color: s.colors.muted, marginTop: 2 }}>
                  {[t.roles[String(u.role).toLowerCase()] || u.role, u.email, u.clientName, u.workingCompany].filter(Boolean).join(" · ")}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RuleValue = ({ rule, onChange }) => {
  const { s, t, language, options } = useBroadcastPage();
  const o = options || { roles: [], clients: [], departments: [], adminLevels: [], groups: [] };
  const L = t.rules;

  switch (rule.type) {
    case "ALL":
      return <div style={{ ...s.input, display: "flex", alignItems: "center", color: s.colors.muted }}>{L.everyActive}</div>;
    case "ROLE":
      return (
        <Select
          value={rule.role}
          onChange={(v) => onChange({ role: v || undefined })}
          placeholder={L.selectRole}
          options={TARGETABLE_ROLES.map((r) => {
            const count = o.roles.find((x) => x.role === r)?.count;
            return { value: r, label: `${t.roles[r]}${count != null ? ` (${count})` : ""}` };
          })}
        />
      );
    case "CLIENT":
      return (
        <Select
          value={rule.clientName}
          onChange={(v) => onChange({ clientName: v || undefined })}
          placeholder={L.selectClient}
          options={o.clients.map((cl) => ({ value: cl.clientName, label: `${cl.clientName} (${cl.count})` }))}
        />
      );
    case "WORKING_COMPANY": {
      const client = o.clients.find((cl) => cl.clientName === rule.clientName);
      return (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8 }}>
          <Select
            value={rule.clientName}
            onChange={(v) => onChange({ clientName: v || undefined, workingCompany: undefined })}
            placeholder={L.client}
            options={o.clients.map((cl) => ({ value: cl.clientName, label: cl.clientName }))}
          />
          <Select
            value={rule.workingCompany}
            disabled={!client}
            onChange={(v) => onChange({ workingCompany: v || undefined })}
            placeholder={L.company}
            options={(client?.workingCompanies || []).map((w) => ({ value: w.name, label: `${w.name} (${w.count})` }))}
          />
        </div>
      );
    }
    case "DEPARTMENT":
      return (
        <Select
          value={rule.departmentId}
          onChange={(v) => onChange({ departmentId: v ? Number(v) : undefined })}
          placeholder={L.selectDepartment}
          options={o.departments.map((d) => ({
            value: d.id,
            label: `${(language === "ar" && d.nameAr) || d.name} — ${d.clientName} / ${d.workingCompany}`,
          }))}
        />
      );
    case "ADMIN_LEVEL":
      return (
        <Select
          value={rule.adminLevelId}
          onChange={(v) => onChange({ adminLevelId: v ? Number(v) : undefined })}
          placeholder={L.selectAdminLevel}
          options={o.adminLevels.map((l) => ({ value: l.id, label: `${l.name} — ${l.clientName} / ${l.workingCompany}` }))}
        />
      );
    case "GROUP":
      return (
        <Select
          value={rule.groupId}
          onChange={(v) => onChange({ groupId: v || undefined })}
          placeholder={L.selectGroup}
          options={o.groups.map((g) => ({ value: g.id, label: g.groupName }))}
        />
      );
    case "USER":
      return <UserPicker rule={rule} onPick={(userId, label) => onChange({ userId, _userLabel: label })} />;
    default:
      return null;
  }
};

const RuleBuilder = ({ rules, onChange }) => {
  const { s, t, screenSize } = useBroadcastPage();
  const L = t.rules;

  const update = (key, patch) => onChange(rules.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const setType = (key, type) => onChange(rules.map((r) => (r._key === key ? { _key: r._key, type, exclude: r.exclude } : r)));
  const removeRule = (key) => onChange(rules.filter((r) => r._key !== key));

  // On phones the row stacks: [include/exclude | delete] then type, then value.
  const stacked = screenSize.isMobile;
  const tablet = screenSize.isTablet;

  return (
    <div>
      {rules.length === 0 && <p style={{ ...s.hint, marginTop: 0, marginBottom: 12 }}>{L.none}</p>}
      {rules.map((r) => {
        const modeSelect = (
          <Select
            ariaLabel={L.includeOrExclude}
            value={r.exclude ? "exclude" : "include"}
            onChange={(v) => update(r._key, { exclude: v === "exclude" })}
            options={[
              { value: "include", label: L.include },
              { value: "exclude", label: L.exclude },
            ]}
          />
        );
        const typeSelect = (
          <Select
            ariaLabel={L.ruleType}
            value={r.type}
            onChange={(v) => setType(r._key, v)}
            options={RULE_TYPES.map((type) => ({ value: type, label: t.ruleTypes[type] }))}
          />
        );
        const del = <IconBtn icon={Trash2} label={L.removeRule} tone="danger" onClick={() => removeRule(r._key)} />;
        const value = <RuleValue rule={r} onChange={(patch) => update(r._key, patch)} />;

        if (stacked || tablet) {
          return (
            <div key={r._key} style={{ ...s.ruleRow(r.exclude), gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto" }}>
              {modeSelect}
              {typeSelect}
              <div style={{ alignSelf: "center" }}>{del}</div>
              <div style={{ gridColumn: "1 / -1" }}>{value}</div>
            </div>
          );
        }
        return (
          <div key={r._key} style={s.ruleRow(r.exclude)}>
            {modeSelect}
            {typeSelect}
            {value}
            <div style={{ alignSelf: "center" }}>{del}</div>
          </div>
        );
      })}
      <div style={{ ...s.row, marginTop: 4 }}>
        <button type="button" className="bc-action-btn" style={s.actionBtn("primary")} onClick={() => onChange([...rules, newRule(rules.length ? "ROLE" : "ALL")])}>
          <Plus size={14} /> {L.addRule}
        </button>
        {rules.length > 0 && (
          <button type="button" className="bc-action-btn" style={s.actionBtn("danger")} onClick={() => onChange([...rules, { ...newRule("USER"), exclude: true }])}>
            <Ban size={14} /> {L.addExclusion}
          </button>
        )}
      </div>
    </div>
  );
};

export default RuleBuilder;
